/* eslint-disable */
import clsx, { ClassValue } from "clsx";
import { enqueueSnackbar } from "notistack";
import { TOAST } from "@/shared/constants/toastMessages";
import { twMerge } from "tailwind-merge";
import posthog from "posthog-js";
import { EventType } from "@/shared/constants/events";
import { supabase } from "@/shared/lib/supabase";
import { getFullName } from "@/shared/utils/formatters";

export { EventType };

type EventTypeValue = (typeof EventType)[keyof typeof EventType];

// =========================================
// POSTHOG TRACKING CONFIGURATION
// =========================================

// Helper to check if we're in production
function isProductionEnvironment(): boolean {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;
  return hostname === "app.elayne.com" || hostname === "elayne-app.vercel.app";
}

type TestUserStatus =
  | { state: "checking"; email: string; result: Promise<boolean> }
  | { state: "known"; email: string; isTest: boolean };

let testUserStatus: TestUserStatus | null = null;
const queuedPostHogCalls: (() => void)[] = [];

function runPostHogCall(call: () => void): void {
  const isKnownTestUser = testUserStatus?.state === "known" && testUserStatus.isTest;
  if (!isProductionEnvironment() || isKnownTestUser) {
    return;
  }
  if (testUserStatus?.state === "checking") {
    queuedPostHogCalls.push(call);
  } else {
    call();
  }
}

function beginTestUserCheck(email: string): TestUserStatus {
  if (testUserStatus === null || testUserStatus.email !== email) {
    testUserStatus = { state: "checking", email, result: checkIsTestUser(email) };
  }
  return testUserStatus;
}

async function resolveIsTestUser(email: string): Promise<boolean | null> {
  const status = beginTestUserCheck(email);
  const isTest = status.state === "known" ? status.isTest : await status.result;
  return testUserStatus?.email === email ? isTest : null;
}

function finishTestUserCheck(email: string, isTest: boolean): void {
  if (testUserStatus?.email === email && testUserStatus.state === "checking") {
    testUserStatus = { state: "known", email, isTest };
    const calls = queuedPostHogCalls.splice(0);
    if (!isTest) {
      calls.forEach((call) => call());
    }
  }
}

// =========================================
// ENHANCED TRACKING FUNCTIONS
// =========================================

export interface TrackingProperties {
  // Common properties for all events
  user_journey_stage?: "acquisition" | "activation" | "engagement" | "retention";
  page_path?: string;
  source?: string;
  medium?: string;
  campaign?: string;

  // Event-specific properties
  task_id?: string;
  task_type?: string;
  task_name?: string;
  task_category?: string;
  document_type?: string;
  error_message?: string;
  step_number?: number;
  success?: boolean;
  started_at?: string;
  completed_at?: string;
  time_to_complete?: number;
  invite_type?: string;
  invited_at?: string;
  recipient_email?: string;

  // Additional context - allow primitive types and arrays
  [key: string]:
    | string
    | number
    | boolean
    | null
    | undefined
    | (string | number | boolean | null)[];
}

/**
 * Track an event with enhanced properties and error handling
 */
export function trackEvent(event: EventTypeValue, properties: TrackingProperties = {}) {
  try {
    const enhancedProperties = {
      ...properties,
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
      page_path: window.location.pathname,
      user_agent: navigator.userAgent,
    };

    const capturedAt = new Date();
    runPostHogCall(() => {
      if (posthog && typeof posthog.capture === "function") {
        posthog.capture(event, enhancedProperties, { timestamp: capturedAt });
      }
    });
  } catch (error) {
    console.error("Failed to track event:", event, error);
  }
}

type PersonPropertyValue = string | number | boolean | null;

export function setPersonProperties(
  set: Record<string, PersonPropertyValue> = {},
  setOnce: Record<string, PersonPropertyValue> = {},
) {
  try {
    runPostHogCall(() => {
      if (posthog && typeof posthog.setPersonProperties === "function") {
        posthog.setPersonProperties(set, setOnce);
      }
    });
  } catch (error) {
    console.error("Failed to set person properties:", error);
  }
}

/**
 * Track user journey progression with automatic stage detection
 */
export function trackJourneyEvent(event: EventTypeValue, properties: TrackingProperties = {}) {
  let stage: TrackingProperties["user_journey_stage"] = "engagement";

  const acquisitionEvents: EventTypeValue[] = [
    EventType.ONBOARDING_STARTED,
    EventType.OBITUARY_SUBMITTED,
    EventType.USER_SIGNED_UP,
    EventType.ACCOUNT_CREATED,
    EventType.ONBOARDING_SCREEN_COMPLETED,
    EventType.PAYWALL_BOUNCE,
  ];
  const activationEvents: EventTypeValue[] = [
    EventType.ONBOARDING_COMPLETED,
    EventType.FIRST_DOCUMENT_UPLOADED,
    EventType.FIRST_TASK_VIEWED,
    EventType.DASHBOARD_ACCESSED,
    EventType.TASK_COMPLETED,
  ];
  const retentionEvents: EventTypeValue[] = [
    EventType.SUBSCRIPTION_STARTED,
    EventType.USER_LOGGED_IN,
    EventType.LOGIN_ATTEMPTED,
    EventType.SUBSCRIPTION_CHANGED,
    EventType.UPGRADE_INITIATED,
  ];

  if (acquisitionEvents.includes(event)) {
    stage = "acquisition";
  } else if (activationEvents.includes(event)) {
    stage = "activation";
  } else if (retentionEvents.includes(event)) {
    stage = "retention";
  }

  trackEvent(event, {
    ...properties,
    user_journey_stage: stage,
  });
}

export function notReadyMessage() {
  enqueueSnackbar(TOAST.COMING_SOON, {
    variant: "info",
  });
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface IdentifyUserParams {
  authUserId?: string;
  firstName?: string;
  surName?: string;
  isPrimaryAccountHolder?: boolean;
  email?: string;
  preferredName?: string;
}

function buildPostHogDisplayName({
  firstName,
  surName,
  email,
}: IdentifyUserParams): string | undefined {
  const fullName = getFullName(firstName ?? null, surName ?? null);
  if (fullName && email) {
    return `${fullName} (${email})`;
  }
  return fullName || email;
}

function identifyInPostHog(userId: string, params: IdentifyUserParams, isInternal: boolean): void {
  if (posthog && typeof posthog.identify === "function") {
    posthog.identify(userId, {
      display_name: buildPostHogDisplayName(params),
      email: params.email,
      first_name: params.firstName,
      last_name: params.surName,
      preferred_name: params.preferredName,
      auth_user_id: params.authUserId,
      is_primary_account_holder: params.isPrimaryAccountHolder,
      is_internal: isInternal,
    });
  }
}

function stopRecordingForTestUser(): void {
  if (posthog && typeof posthog.stopSessionRecording === "function") {
    posthog.stopSessionRecording();
  }
}

export async function identifyUser(params: IdentifyUserParams) {
  const normalizedEmail = params.email?.trim().toLowerCase();
  const userId = normalizedEmail || params.authUserId;
  if (!userId) return;

  const isTest = normalizedEmail ? await resolveIsTestUser(normalizedEmail) : false;
  if (isTest !== null) {
    try {
      if (isTest) {
        stopRecordingForTestUser();
      }
      // Test users are identified too, so the "Internal users" cohort can exclude their anonymous pre-login events
      identifyInPostHog(userId, params, isTest);
    } catch (error) {
      console.error("Failed to identify user:", error);
    } finally {
      if (normalizedEmail) {
        finishTestUserCheck(normalizedEmail, isTest);
      }
    }
  }
}

const TEST_DOMAINS = ["@elayne.com", "@meetelayne.com"];
const TEST_USER_CHECK_TIMEOUT_MS = 4000;

async function checkIsTestUser(email: string): Promise<boolean> {
  const normalized = email.trim().toLowerCase();
  if (TEST_DOMAINS.some((domain) => normalized.endsWith(domain))) return true;

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<null>((resolve) => {
      timeoutId = setTimeout(() => resolve(null), TEST_USER_CHECK_TIMEOUT_MS);
    });
    const result = await Promise.race([
      supabase.functions.invoke("account/is-test-user", { body: { email } }),
      timeout,
    ]);
    if (!result) {
      console.warn("Test user check timed out, defaulting to not-test");
      return false;
    }
    if (result.error) {
      console.warn("Test user check failed, defaulting to not-test:", result.error);
      return false;
    }
    return (result.data as { isTestUser: boolean } | null)?.isTestUser ?? false;
  } catch {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}
